import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { IBlackJackGame, IGameResult, IPenaltyItem } from "../ts/Definitions";
import { SelectedSettingsContext } from "../ts/Contexts";

interface BlackJackDialogProps {
  game: IBlackJackGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

type Card = { value: number; display: string };
type Hand = Card[];

function getRandomCard(): Card {
  const values = [
    { value: 11, display: "A" },
    { value: 2, display: "2" },
    { value: 3, display: "3" },
    { value: 4, display: "4" },
    { value: 5, display: "5" },
    { value: 6, display: "6" },
    { value: 7, display: "7" },
    { value: 8, display: "8" },
    { value: 9, display: "9" },
    { value: 10, display: "10" },
    { value: 10, display: "J" },
    { value: 10, display: "Q" },
    { value: 10, display: "K" },
  ];
  return values[Math.floor(Math.random() * values.length)];
}

function handValue(hand: Hand): number {
  let total = hand.reduce((sum, card) => sum + card.value, 0);
  let aces = hand.filter(card => card.value === 11).length;
  while (total > 21 && aces > 0) {
    total -= 10;
    aces--;
  }
  return total;
}

function isBlackjack(hand: Hand): boolean {
  return hand.length === 2 && handValue(hand) === 21;
}

const PlayingCard: React.FC<{ rank: string; suit?: string }> = ({ rank, suit }) => {
  // Use suit if provided, else determine from rank (for Blackjack, suit is not used)
  // For visual, alternate colors for face cards and Ace
  const isRed = rank === "A" || rank === "J" || rank === "Q" || rank === "K";
  return (
    <Box
      sx={{
        width: 60,
        height: 90,
        borderRadius: 2,
        border: "2px solid #222",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        p: 1,
        mx: "auto",
        mb: 1,
        position: "relative",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: isRed ? "#d32f2f" : "#222",
          position: "absolute",
          top: 8,
          left: 10,
          fontWeight: "bold",
        }}
      >
        {rank}
      </Typography>
      <Typography
        variant="h3"
        sx={{
          color: isRed ? "#d32f2f" : "#222",
          fontWeight: "bold",
          mt: 2,
        }}
      >
        {rank}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: isRed ? "#d32f2f" : "#222",
          position: "absolute",
          bottom: 8,
          right: 10,
          fontWeight: "bold",
        }}
      >
        {rank}
      </Typography>
    </Box>
  );
};

const BlackJackDialog: React.FC<BlackJackDialogProps> = ({ game, onClose, penaltyKeys }) => {
  const settings = useContext(SelectedSettingsContext);
  const { rounds, minPassRate, key, userid, username } = game;
  const [currentRound, setCurrentRound] = useState(1);
  const [playerWins, setPlayerWins] = useState(0);
  const [dealerWins, setDealerWins] = useState(0);
  const [draws, setDraws] = useState(0);
  const [finished, setFinished] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [playerHand, setPlayerHand] = useState<Hand>([]);
  const [dealerHand, setDealerHand] = useState<Hand>([]);
  const [playerTurn, setPlayerTurn] = useState(true);
  const [playerStands, setPlayerStands] = useState(false);

  const [penalties, setPenalties] = useState<(IPenaltyItem)[]>([]);

  useEffect(() => {
    const newPens = penaltyKeys?.map(key => settings.getPenalty(key)).filter(p => p !== undefined) as IPenaltyItem[] || [];
    setPenalties(newPens);
  }, [penaltyKeys]);

  // Start a new round
  const startRound = () => {
    setPlayerHand([getRandomCard(), getRandomCard()]);
    setDealerHand([getRandomCard(), getRandomCard()]);
    setPlayerTurn(true);
    setPlayerStands(false);
    setMessage(null);
  };

  // On mount and each new round
  React.useEffect(() => {
    if (!finished) startRound();
    // eslint-disable-next-line
  }, [currentRound, finished]);

  // Player hits
  const handleHit = () => {
    if (!playerTurn || finished) return;
    setPlayerHand(prev => [...prev, getRandomCard()]);
  };

  // Player stands
  const handleStand = () => {
    setPlayerTurn(false);
    setPlayerStands(true);
  };

  // Dealer logic after player stands or busts
  React.useEffect(() => {
    if (!playerTurn && playerStands && !finished) {
      let dealer = [...dealerHand];
      while (handValue(dealer) < 17) {
        dealer.push(getRandomCard());
      }
      setDealerHand(dealer);

      setTimeout(() => {
        const playerVal = handValue(playerHand);
        const dealerVal = handValue(dealer);

        if (playerVal > 21) {
          setDealerWins(w => w + 1);
          setMessage("You bust! Dealer wins this round.");
        } else if (dealerVal > 21) {
          setPlayerWins(w => w + 1);
          setMessage("Dealer busts! You win this round.");
        } else if (playerVal > dealerVal) {
          setPlayerWins(w => w + 1);
          setMessage("You win this round!");
        } else if (dealerVal > playerVal) {
          setDealerWins(w => w + 1);
          setMessage("Dealer wins this round!");
        } else {
          setDraws(d => d + 1);
          setMessage("Draw!");
        }
        setTimeout(() => nextRound(), 1200);
      }, 800);
    }
    // eslint-disable-next-line
  }, [playerTurn, playerStands]);

  // Player busts
  React.useEffect(() => {
    if (playerTurn && handValue(playerHand) > 21 && !finished) {
      setPlayerTurn(false);
      setPlayerStands(true);
    }
    // eslint-disable-next-line
  }, [playerHand]);

  const nextRound = () => {
    if (currentRound >= rounds) {
      setFinished(true);
      setMessage(
        `Game Over! You won ${playerWins} of ${rounds} rounds. Pass rate required: ${minPassRate}`
      );
    } else {
      setCurrentRound(r => r + 1);
      setPlayerHand([]);
      setDealerHand([]);
      setPlayerTurn(true);
      setPlayerStands(false);
      setMessage(null);
    }
  };

  const handleClose = () => {
    onClose({
      gameKey: key,
      penaltyCount: rounds - dealerWins >= minPassRate ? 0 : minPassRate - (rounds - dealerWins),
      completed: playerWins,
      total: rounds,
      totalTime: rounds,
      mistakes: rounds - playerWins,
      userid,
      username,
      date: new Date(),
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>Blackjack</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Win at least {minPassRate} out of {rounds} rounds!
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {penalties.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Penalty for incorrect answers: <b>{penalties.map(p => p.name).join(", ")}</b>
            </Alert>
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Round: {currentRound} / {rounds}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Your Wins: {playerWins}</Typography>
          <Typography variant="body2">Dealer Wins: {dealerWins}</Typography>
          <Typography variant="body2">Draws: {draws}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Dealer's Hand:</Typography>
          <Grid container spacing={1} justifyContent="center">
            {dealerHand.map((card, idx) => (
              <Grid item key={idx}>
                <PlayingCard rank={card.display} />
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Value: {handValue(dealerHand)}
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Your Hand:</Typography>
          <Grid container spacing={1} justifyContent="center">
            {playerHand.map((card, idx) => (
              <Grid item key={idx}>
                <PlayingCard rank={card.display} />
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Value: {handValue(playerHand)}
          </Typography>
        </Box>
        {message && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" color={finished && playerWins >= minPassRate ? "success.main" : finished ? "error.main" : "text.primary"}>
              {message}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {finished && (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Close
          </Button>
        )}
        {!finished && playerTurn && (
          <>
            <Button variant="contained" color="primary" onClick={handleHit}>
              Hit
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleStand}>
              Stand
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BlackJackDialog;